import { SyntheticEvent, useCallback, useState } from "react";

export type AlertType = "error" | "success" | "info" | "warning";

export interface AlertState {
  open: boolean;
  type: AlertType;
  message: string;
}

export interface AlertQueueHandlers {
  alertState: AlertState;
  queue: string[];
  showAlert: (message: string, type?: AlertType) => void;
  enqueueMessages: (messages: string[]) => void;
  handleClose: (event?: SyntheticEvent | Event, reason?: string) => void;
  showNextFromQueue: () => void;
  clearAlerts: () => void;
}

const DEFAULT_ALERT_STATE: AlertState = {
  open: false,
  type: "error",
  message: "",
};

/**
 * Centralized alert queue management for GRN flows.
 * Most GRN screens fire sequential API validations and need a consistent UX
 * to surface multiple errors. This hook extracts the queue/alert orchestration.
 */
export const useAlertQueue = (
  initialType: AlertType = "error"
): AlertQueueHandlers => {
  const [alertState, setAlertState] = useState<AlertState>({
    ...DEFAULT_ALERT_STATE,
    type: initialType,
  });
  const [queue, setQueue] = useState<string[]>([]);

  const showNextFromQueue = useCallback(() => {
    setQueue((prev) => {
      if (!prev.length) {
        setAlertState((current) => ({ ...current, open: false, message: "" }));
        return prev;
      }
      const [next, ...rest] = prev;
      setAlertState((current) => ({
        ...current,
        open: true,
        message: next,
      }));
      return rest;
    });
  }, []);

  const showAlert = useCallback(
    (message: string, type?: AlertType) => {
      setAlertState((current) => ({
        open: true,
        message,
        type: type ?? current.type,
      }));
    },
    [] // No dependencies - use functional update to access current type
  );

  const enqueueMessages = useCallback((messages: string[]) => {
    if (!messages?.length) return;
    setQueue((prev) => [...prev, ...messages.filter(Boolean)]);
  }, []);

  const handleClose = useCallback(
    (_event?: SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") return;
      setAlertState((current) => ({ ...current, open: false }));
      // Slight timeout avoids the snackbar flash while closing.
      setTimeout(() => {
        showNextFromQueue();
      }, 150);
    },
    [showNextFromQueue]
  );

  const clearAlerts = useCallback(() => {
    setQueue([]);
    setAlertState((current) => ({ ...current, open: false, message: "" }));
  }, []);

  return {
    alertState,
    queue,
    showAlert,
    enqueueMessages,
    handleClose,
    showNextFromQueue,
    clearAlerts,
  };
};
